import { createContext, use, useId, useLayoutEffect, useRef } from "react";

import type { StoreApi } from "zustand";

import type { EditToolbarAction } from "./EditToolbar";
import type { EditChange } from "./changes";

type Section = {
  title?: string;
  changes: EditChange[];
  changed: boolean;
  saving: boolean;
  save: () => void;
  discard?: () => void;
  actions: EditToolbarAction[];
};

export type SectionsStore = StoreApi<{
  sections: Record<string, Section>;
  /** Save labels of the nested `EditSections`, by how deeply they're nested. */
  saveLabels: Record<string, { depth: number; label: string }>;
}>;

export const SectionsContext = createContext<{
  store: SectionsStore;
  depth: number;
} | null>(null);

type UseEditSectionOptions = {
  /** Heads this section's changes in the unsaved changes dialog. */
  title?: string;
  /** What's unsaved, in words. See `./changes.ts`. */
  changes?: EditChange[];
  changed: boolean;
  saving?: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  actions?: EditToolbarAction[];
};

/** Registers part of a page with the toolbar of the nearest `EditSections`. */
export function useEditSection({
  title,
  changes = [],
  changed,
  saving = false,
  onSave,
  onDiscard,
  actions = [],
}: UseEditSectionOptions) {
  const store = use(SectionsContext)?.store;
  if (!store) {
    throw new Error("useEditSection must be used inside <EditSections>");
  }
  const id = useId();
  const handlers = useRef({ onSave, onDiscard });

  useLayoutEffect(() => {
    handlers.current = { onSave, onDiscard };
    store.setState((state) => ({
      sections: {
        ...state.sections,
        [id]: {
          title,
          changes,
          changed,
          saving,
          save: () => handlers.current.onSave(),
          discard: () => handlers.current.onDiscard?.(),
          actions,
        },
      },
    }));
  });

  useLayoutEffect(
    () => () =>
      store.setState((state) => {
        const sections = { ...state.sections };
        delete sections[id];
        return { sections };
      }),
    [store, id],
  );
}
